import styled from "styled-components";
import { SampleButton } from "./SampleButton.tsx";
import { Input } from "./Input.tsx";
import { Stack } from "./Stack.tsx";
import { Button } from "./Button.tsx";
import { SampleVideoPlayer, SAMPLE_PADDING_BEFORE } from "./SampleVideoPlayer.tsx";
import type { SpeakerRun } from "../../lib/samples.ts";
import type { SpeakerProfile } from "../../lib/types.ts";

// ---------------------------------------------------------------------------
// Configuración de reproducción de samples (Fase 24).
//
// Estos valores DEBEN coincidir con los que IdentifyScreen.handlePlaySample
// pasa a useSamplePlayer.play(). Si los cambiás acá, cambialos también allá.
// (Si en el futuro empieza a divergir esta config en otras pantallas,
// conviene centralizarla en un módulo compartido.)
// ---------------------------------------------------------------------------

const SAMPLE_PADDING_AFTER = 2;
const SAMPLE_CHUNK_DURATION = 5;

type SpeakerCardProps = {
  speakerIndex: number;
  /** True si fue agregado manualmente (índice < 0). Cambia el aspecto. */
  isManual?: boolean;
  segmentCount: number;
  segmentTotal: number;
  samples: SpeakerRun[];
  profile: SpeakerProfile | undefined;
  playingId: string | null;
  progress: number;
  /**
   * URL de PAR del video MP4. Presente solo cuando el procesamiento tiene
   * audio.mp4. Cuando está definida, el sample que esté sonando muestra un
   * <video> inline debajo del SampleButton (sin controles propios — el play
   * es el del SampleButton).
   */
  videoUrl?: string | null;
  /**
   * Contador que cambia en cada click de play. Se usa como parte del `key`
   * del SampleVideoPlayer para forzar remount cuando el usuario re-clickea
   * el sample que ya está sonando (Replay = re-arranque desde el inicio).
   */
  playKey?: number;
  onPlaySample: (sampleId: string, startTime: number, duration: number) => void;
  onChangeProfile: (patch: Partial<SpeakerProfile>) => void;
  /** Solo presente en cards manuales: callback para eliminar este speaker. */
  onRemove?: () => void;
};

export function SpeakerCard({
  speakerIndex,
  isManual = false,
  segmentCount,
  segmentTotal,
  samples,
  profile,
  playingId,
  progress,
  videoUrl,
  playKey = 0,
  onPlaySample,
  onChangeProfile,
  onRemove,
}: SpeakerCardProps) {
  const pct = segmentTotal > 0 ? (segmentCount / segmentTotal) * 100 : 0;
  const isComplete =
    profile !== undefined &&
    profile.name.trim() !== "" &&
    profile.role.trim() !== "";

  const previewOf = (run: SpeakerRun): string => {
    const words = run.tokens.slice(0, 8).map((t) => t.token).join(" ");
    return run.tokens.length > 8 ? `${words}…` : words;
  };

  // Etiqueta del header. Para manuales no mostramos "Speaker N" sino algo
  // más amigable.
  const titleText = isManual
    ? `Speaker manual ${Math.abs(speakerIndex)}`
    : `Speaker ${speakerIndex}`;

  return (
    <Card $complete={isComplete} $manual={isManual}>
      <Header>
        <SpeakerLabel>
          <Badge $complete={isComplete} $manual={isManual}>
            {isComplete ? "✓" : isManual ? "+" : speakerIndex}
          </Badge>
          <SpeakerTitle>{titleText}</SpeakerTitle>
          {isManual && <ManualTag>Agregado manualmente</ManualTag>}
        </SpeakerLabel>

        {!isManual && (
          <Stats>
            {segmentCount} segmentos · {pct.toFixed(0)}% del habla
          </Stats>
        )}

        {isManual && onRemove && (
          <Button $variant="ghost" onClick={onRemove}>
            Eliminar
          </Button>
        )}
      </Header>

      {!isManual && (
        <Stack $gap="xs">
          {samples.length === 0 ? (
            <NoSamples>
              No hay muestras representativas con los filtros default.
            </NoSamples>
          ) : (
            samples.map((s, i) => {
              const id = `${speakerIndex}-${i}`;
              const isPlaying = playingId === id;
              const showVideo = isPlaying && Boolean(videoUrl);
              return (
                <SampleWrapper key={id}>
                  <SampleButton
                    startTime={s.startTime}
                    duration={s.duration}
                    preview={previewOf(s)}
                    isPlaying={isPlaying}
                    progress={isPlaying ? progress : 0}
                    onPlay={() =>
                      onPlaySample(id, s.startTime, SAMPLE_CHUNK_DURATION)
                    }
                    paddingBefore={SAMPLE_PADDING_BEFORE}
                    paddingAfter={SAMPLE_PADDING_AFTER}
                    chunkDuration={SAMPLE_CHUNK_DURATION}
                  />
                  {showVideo && videoUrl && (
                    <SampleVideoPlayer
                      key={`${id}-${playKey}`}
                      src={videoUrl}
                      startTime={s.startTime}
                    />
                  )}
                </SampleWrapper>
              );
            })
          )}
        </Stack>
      )}

      {isManual && (
        <ManualNote>
          Este speaker no fue detectado por Oracle. Asígnalo manualmente a
          segmentos en la pantalla de revisión cuando reconozcas su voz.
        </ManualNote>
      )}

      <FieldsGrid>
        <Input
          label="Nombre"
          placeholder="Ej. María García"
          value={profile?.name ?? ""}
          onChange={(e) => onChangeProfile({ name: e.target.value })}
        />
        <Input
          label="Cargo"
          placeholder="Ej. Líder de Producto"
          value={profile?.role ?? ""}
          onChange={(e) => onChangeProfile({ role: e.target.value })}
        />
      </FieldsGrid>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Card = styled.div<{ $complete: boolean; $manual: boolean }>`
  background: ${({ theme, $manual }) =>
    $manual ? theme.color.background : theme.color.surface};
  border: 1px ${({ $manual }) => ($manual ? "dashed" : "solid")}
    ${({ theme, $complete, $manual }) =>
      $complete
        ? theme.color.success
        : $manual
          ? theme.color.borderStrong
          : theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
  transition: border-color 120ms;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
`;

const SpeakerLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const Badge = styled.div<{ $complete: boolean; $manual: boolean }>`
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  background: ${({ theme, $complete, $manual }) =>
    $complete
      ? theme.color.success
      : $manual
        ? theme.color.warningMuted
        : theme.color.background};
  color: ${({ theme, $complete, $manual }) =>
    $complete ? "white" : $manual ? theme.color.warning : theme.color.textMuted};
  border: 1px solid
    ${({ theme, $complete, $manual }) =>
      $complete
        ? theme.color.success
        : $manual
          ? theme.color.warning
          : theme.color.border};
`;

const SpeakerTitle = styled.div`
  font-size: ${({ theme }) => theme.font.size.lg};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const ManualTag = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.warning};
  background: ${({ theme }) => theme.color.warningMuted};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const Stats = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.mono};
`;

const NoSamples = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
  padding: ${({ theme }) => theme.space.sm} 0;
`;

const ManualNote = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
  padding: ${({ theme }) => theme.space.sm};
  background: white;
  border-radius: ${({ theme }) => theme.radius.sm};
  border-left: 3px solid ${({ theme }) => theme.color.warning};
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.md};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

/** Envuelve el SampleButton + el video player de ese chunk. */
const SampleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;



